import { useTranslation } from 'react-i18next';
import { useComments } from 'entities/comment';
import { useMemo, useState, useEffect, useRef } from 'react';
import { serialize } from 'slate/compareUtils';
import { useIsViewingVersion } from 'app/router/params';
import { useSidebar } from 'app/state/contexts/SidebarContext';
import { useSlate } from 'slate-react';
import { Comment } from './Comment';
import moment from 'moment';
import { useEditorLiveSection } from 'app/state/contexts/EditorUiContext';
import { revealCommentCard } from './comment-jump';
import { CommentItem } from './types';

export const Comments = () => {
  const { t } = useTranslation(['common', 'doc-editor-link']);

  const { superId: sectionSuperId, content } = useEditorLiveSection();
  const [activeResponse, setActiveResponse] = useState<CommentItem | null>(null);
  const [includeClosedComments, setIncludeClosedComments] = useState(false);
  const { comments, isCommentsLoading } = useComments(sectionSuperId) as { comments: CommentItem[] | undefined; isCommentsLoading: boolean };
  const { draftComment, setActiveComment, activeComment } = useSidebar();
  const isViewingVersion = useIsViewingVersion();
  const revealedComment = useRef<string | null>(null);

  const editor = useSlate();

  const reorderedComments = useMemo(() => {
    let flatDocument: Record<string, unknown> = {};
    if (content && Object.keys(content).length !== 0) {
      flatDocument = serialize({ content });
    }
    if (isCommentsLoading) {
      return [];
    }

    let orderedComments = Object.entries(flatDocument).flatMap(
      ([key]) => comments?.filter((comment) => comment.onId === key && comment.onType !== 'comment') ?? []
    );

    const anchorIds = new Set(Object.keys(flatDocument));
    if (anchorIds.size > 0) {
      const orphanedComments =
        comments
          ?.filter((comment) => comment.onType !== 'comment' && comment.closedAt && !anchorIds.has(comment.onId ?? ''))
          .map((comment) => ({ ...comment, isOrphaned: true })) ?? [];
      orderedComments = [...orderedComments, ...orphanedComments];
    }

    if (!includeClosedComments) {
      orderedComments = orderedComments.filter((comment) => !comment.closedAt);
    }

    const commentsWithResponses = orderedComments.map((comment) => {
      const responses =
        comments
          ?.filter((response) => response.onType === 'comment' && response.onId === comment.id)
          .sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()) || [];
      return { ...comment, responses };
    });
    return commentsWithResponses;
  }, [content, isCommentsLoading, comments, includeClosedComments]);

  // Reveals whatever is active, which covers the cards CommentJump activates without a click.
  useEffect(() => {
    const cardId = activeComment?.id;
    if (!cardId || revealedComment.current === cardId) return;
    revealedComment.current = cardId;
    if (activeComment?.closedAt) setIncludeClosedComments(true);

    return revealCommentCard(cardId);
  }, [activeComment]);

  useEffect(() => {
    if (draftComment) {
      setActiveComment(draftComment);
    }
    return () => {
      setActiveComment(null);
      setActiveResponse(null);
    };
  }, [editor, draftComment, setActiveComment]);
  const noComments = reorderedComments.length === 0 && !isCommentsLoading && !draftComment;
  return (
    <div className="preview--container h-100">
      <div className="px-3 pt-4 pb-3 w-100 bg-white sticky-top">
        <div className="fw-bold">{t('doc-editor-link:sidebar.comments-tooltip')}</div>
        <div className="comment--list--filter py-3">
          <p>{t('doc-editor-link:sidebar.comment.show-closed')}</p>
          <div className="form-switch">
            <input
              className="form-check-input"
              checked={includeClosedComments}
              onChange={() => setIncludeClosedComments(!includeClosedComments)}
              type="checkbox"
              role="switch"
            />
          </div>
        </div>
      </div>
      <div className={`comment--list ${noComments ? 'no-comments' : ''}`}>
        {noComments && (
          <div className="" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <i className="fa-regular fa-message-xmark" />
            {t('doc-editor-link:sidebar.no-comments')}
          </div>
        )}
        {draftComment && <Comment comment={draftComment as CommentItem} key={draftComment.id} />}
        {isCommentsLoading ? (
          <div>{t('common:loading-message')}</div>
        ) : (
          reorderedComments.map((comment) => (
            <Comment
              key={comment.id}
              comment={comment}
              responses={comment.responses}
              activeResponse={activeResponse}
              setActiveResponse={setActiveResponse}
              readOnly={isViewingVersion}
            />
          ))
        )}
      </div>
    </div>
  );
};
